import type { FC, ReactNode } from "react";
import React, { useCallback } from "react";

import { useDispatch } from "react-redux";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { showErrorToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";

export interface IPromptCardProps {
    children?: ReactNode;
    text?: string;
    textFooterElements?: ReactNode[];
    dataAutomation?: string;
    copyToClipboard?: boolean;
    borderless?: boolean;
}

const CardBox = styled(Box)<{ $hoverEnabled: boolean }>`
    transition:
        background-color 120ms ease,
        border-color 120ms ease;
    ${({ $hoverEnabled }) =>
        $hoverEnabled
            ? `
            cursor: pointer;
        &:hover {
            background-color: ${colorsPalettes.carbon[25]};
            ${SWReactIcons}{
                path{
                    fill: ${colorsPalettes.blue[400]};
                }
            }
        }
    `
            : ""}
`;

export const PromptCard: FC<IPromptCardProps> = ({
    children,
    text,
    textFooterElements,
    dataAutomation = "prompt-card",
    copyToClipboard,
    borderless,
}) => {
    const dispatch = useDispatch();

    const handleCopy = useCallback(() => {
        if (!text) {
            return;
        }

        void navigator.clipboard
            .writeText(text)
            .then(() => {
                dispatch(showErrorToast(translate("analysis.ai.traffic.prompts.copy.toast")));
            })
            .catch(() => {
                dispatch(showErrorToast(translate("analysis.ai.traffic.prompts.copy.toast")));
            });
    }, [dispatch, text]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleCopy();
            }
        },
        [handleCopy],
    );

    const hoverEnabled = React.Children.count(children) === 0;

    return (
        <CardBox
            border={borderless ? "none" : `1px solid ${colorsPalettes.carbon[50]}`}
            padding={borderless ? "0" : "16px"}
            borderRadius={borderless ? "0" : "4px"}
            width="100%"
            minWidth="0"
            data-automation={dataAutomation}
            $hoverEnabled={hoverEnabled}
            onClick={copyToClipboard ? handleCopy : undefined}
            onKeyDown={copyToClipboard ? handleKeyDown : undefined}
        >
            <Flex
                gap="16px"
                alignItems="flex-start"
                flexDirection="column"
                width="100%"
            >
                {text ? (
                    <Flex
                        gap="8px"
                        alignItems="flex-start"
                        width="100%"
                        justifyContent="space-between"
                    >
                        <Text
                            fontVariant="body2"
                            color={colorsPalettes.carbon[500]}
                        >
                            {text}
                        </Text>
                        {copyToClipboard ? (
                            <Flex
                                tabIndex={0}
                                aria-label={translate("analysis.ai.traffic.prompts.copy.tooltip")}
                                alignItems="center"
                                justifyContent="center"
                            >
                                <SWReactIcons
                                    iconName="copy-rounded"
                                    size="xs"
                                />
                            </Flex>
                        ) : null}
                    </Flex>
                ) : (
                    <Text
                        fontVariant="body2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {text}
                    </Text>
                )}
                {textFooterElements?.length ? (
                    <Flex
                        flexDirection="row"
                        gap="6px"
                    >
                        {textFooterElements}
                    </Flex>
                ) : null}
                {children}
            </Flex>
        </CardBox>
    );
};
